/**
 * Skeleton primitives: the grey shapes a screen wears while its first read is in
 * flight.
 *
 * WHY A SKELETON RATHER THAN A SPINNER OR A PARTIAL PAINT. The wallet fans out one
 * balance read per wallet, so a screen that renders whatever has landed shows a
 * portfolio total that climbs, rows that pop in, and an empty state that flashes
 * before the real list arrives. Every one of those reads as a bug to the person
 * holding the phone. A skeleton states the shape of what is coming and commits to
 * nothing until all of it is here.
 *
 * THE SHAPE IS THE POINT. A skeleton is worth having only if it occupies roughly
 * the space the real content will, so the page does not jump when the data lands.
 * These primitives take explicit sizes for that reason, and each screen composes
 * its own arrangement from them rather than sharing one generic block.
 *
 * ACCESSIBILITY. The shapes are decorative and carry no text, so a screen reader
 * must not walk them: every skeleton is mounted inside a SkeletonScreen, which
 * marks the region busy and hides the shapes, and announces one honest "Loading"
 * status instead.
 */

import type { CSSProperties, ReactNode } from 'react';

import { useT } from '@/i18n';

/** A single grey line, for a run of text. */
export function SkeletonLine({
  width,
  height,
}: {
  /** Any CSS length or percentage. Defaults to the full width of the row. */
  width?: string;
  /** Any CSS length. Defaults to a body line. */
  height?: string;
}) {
  const style: CSSProperties = {};
  if (width) style.inlineSize = width;
  if (height) style.blockSize = height;
  return <span className="skeleton__line" style={style} />;
}

/** A circle, for an avatar or a coin mark. */
export function SkeletonCircle({ size = '2.25rem' }: { size?: string }) {
  return <span className="skeleton__circle" style={{ inlineSize: size, blockSize: size }} />;
}

/** A filled rectangle, for a card, a QR, a chart, or a button. */
export function SkeletonBlock({ height, width }: { height: string; width?: string }) {
  const style: CSSProperties = { blockSize: height };
  if (width) style.inlineSize = width;
  return <span className="skeleton__block" style={style} />;
}

/** A leading mark with two stacked lines beside it — the shape of a list row. */
export function SkeletonRow({ markSize = '2.25rem' }: { markSize?: string }) {
  return (
    <div className="skeleton__row">
      <SkeletonCircle size={markSize} />
      <span className="skeleton__row-lines">
        <SkeletonLine width="55%" />
        <SkeletonLine width="35%" height="0.625rem" />
      </span>
      <span className="skeleton__row-trailing">
        <SkeletonLine width="4.5rem" />
        <SkeletonLine width="3rem" height="0.625rem" />
      </span>
    </div>
  );
}

/** A bordered card wrapping arbitrary skeleton shapes. */
export function SkeletonCard({ children }: { children: ReactNode }) {
  return <div className="skeleton__card">{children}</div>;
}

/**
 * The wrapper every screen skeleton is mounted in.
 *
 * It owns the two things that must not be re-decided per screen: the pulse, and
 * the accessibility contract. `aria-busy` tells assistive tech the region is not
 * final; `aria-hidden` on the shapes keeps a screen reader from reading a wall of
 * empty spans; and the visually hidden status line gives it the one thing worth
 * announcing.
 */
export default function SkeletonScreen({
  children,
  label,
}: {
  children: ReactNode;
  /** Overrides the generic "Loading" announcement, e.g. "Loading your wallets". */
  label?: string;
}) {
  const t = useT();
  const text = label ?? t('common.loading');
  return (
    <div className="skeleton" aria-busy="true">
      <span className="visually-hidden" role="status">
        {text}
      </span>
      <div className="skeleton__shapes" aria-hidden="true">
        {children}
      </div>
    </div>
  );
}
